import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface NeuraQuestionAskerProps {
  question: string;
  options?: string[];
  onSubmit: (answer: string) => void;
  system?: string; // Optional context icon/theme
}

const NeuraQuestionAsker: React.FC<NeuraQuestionAskerProps> = ({ question, options, onSubmit, system }) => {
  const [customInput, setCustomInput] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (answer: string) => {
    if (!answer.trim() || submitted) return;
    setSubmitted(true);
    onSubmit(answer.trim());
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-[#0a0a0a] border border-gray-100 dark:border-gray-800 rounded-2xl p-5 shadow-sm max-w-md w-full my-3 flex flex-col gap-4"
    >
      <div className="flex items-start gap-3">
        <div className="bg-gray-50 dark:bg-[#111] border border-gray-100 dark:border-gray-800 p-2 rounded-xl flex-shrink-0 mt-0.5">
          <svg className="w-4 h-4 text-black dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h4 className="text-black dark:text-white font-medium text-[14px] leading-relaxed">{question}</h4>
        </div>
      </div>

      {options && options.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-1">
          {options.map((option, idx) => (
            <button
              key={idx}
              onClick={() => handleSubmit(option)}
              disabled={submitted}
              className="px-4 py-2 bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#111] text-black dark:text-white text-[13px] font-medium rounded-full transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {option}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-2">
        <input
          type="text"
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit(customInput)}
          disabled={submitted}
          placeholder="Type your answer..."
          className="flex-1 bg-gray-50 dark:bg-[#111] border border-gray-200 dark:border-gray-800 focus:border-black dark:focus:border-white rounded-xl px-4 py-2.5 text-[13px] text-black dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none transition-all disabled:opacity-50 shadow-inner"
        />
        <button
          onClick={() => handleSubmit(customInput)}
          disabled={!customInput.trim() || submitted}
          className="bg-black dark:bg-white text-white dark:text-black px-4 py-2.5 rounded-xl text-[13px] font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center hover:opacity-90 shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </button>
      </div>
    </motion.div>
  );
};

export default NeuraQuestionAsker;
